"use client";

// useSeamMask — the head has no background; the CONTENT lets go under it
// (law_one_ground, trial of 2026-09-26, dashboard only).
//
// The pinned navigator used to hide what scrolled under it with a band of its
// own: a frost (::before) and a tint of the ground (::after) masked to the
// band, cut away over netti's seat on the right so the creature stayed whole.
// Three gradients in three directions, and on a night ground you could see
// every one of them — the band a shade off the room, lighter on the right
// where it let go, a second veil in each corner.
//
// Here there is one ground and nothing painted over it. The content itself is
// masked: transparent from the top of the screen down to the head's seam, then
// opaque a few px below it — so whatever sits behind (the ground, netti) simply
// shows, and the words dissolve into it as they pass up under the head. The
// only layer left in the head is a thin progressive blur AT the seam.
//
// The mask is a gradient sized to the viewport's frame of reference: its image
// is offset by the content's own distance from the top of the screen (written
// here as `--mask-y`, a non-inheriting registered property, so a scroll frame
// restyles one element and none of its subtree). Where the seam sits is the
// head's layout bottom (`--seam-open`, constant once the head locks); the fold
// moves it up by the folded height, and that motion is CSS's (`--seam-fold`
// eases with the head's own fold — see globals.css, THE SEAM).

import { useEffect, type RefObject } from "react";

export function useSeamMask(
  head: RefObject<HTMLElement | null>,
  content: RefObject<HTMLElement | null>,
  opts: { active: boolean; key?: string },
): void {
  const { active, key } = opts;
  useEffect(() => {
    const root = document.documentElement;
    if (!active) return;
    root.setAttribute("data-os-seam", "");
    let frame = 0;
    let lastY = NaN;
    let lastSeam = NaN;
    let lastShift = NaN;
    let remeasure = true;
    const write = () => {
      frame = 0;
      const h = head.current;
      const c = content.current;
      if (!h || !c) return;
      const hr = h.getBoundingClientRect();
      const cr = c.getBoundingClientRect();
      const seam = Math.round(hr.bottom);
      const y = Math.round(-cr.top);
      if (y !== lastY) { lastY = y; c.style.setProperty("--mask-y", `${y}px`); }
      if (seam !== lastSeam) { lastSeam = seam; c.style.setProperty("--seam-open", `${seam}px`); }
      // the folded height is the head's own measure (useNaturalHeight writes
      // --lock-h / --lock-gap on it); read on layout changes, not per frame
      if (!remeasure) return;
      remeasure = false;
      const cs = getComputedStyle(h);
      const shift = Math.round((parseFloat(cs.getPropertyValue("--lock-h")) || 0) + (parseFloat(cs.getPropertyValue("--lock-gap")) || 0));
      if (shift !== lastShift) { lastShift = shift; c.style.setProperty("--seam-shift", `${shift}px`); }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(write); };
    const relayout = () => { remeasure = true; schedule(); };
    write();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", relayout);
    const ro = new ResizeObserver(relayout);
    if (head.current) ro.observe(head.current);
    if (content.current) ro.observe(content.current);
    // the fold flips an attribute on the head; the seam's CSS reads it, but the
    // head's measure can change with it (a hover opening it), so re-measure
    const mo = new MutationObserver(relayout);
    if (head.current) mo.observe(head.current, { attributes: true, attributeFilter: ["data-folded", "data-expand", "style"] });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", relayout);
      ro.disconnect();
      mo.disconnect();
      root.removeAttribute("data-os-seam");
    };
  }, [head, content, active, key]);
}
